import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { type Product } from "@/lib/types";

/**
 * Shop cart — the app's only cross-page store. Booking keeps its cart in
 * component state, but the shop needs a header badge that reacts from any
 * page, hence a real provider persisted to localStorage.
 */
export interface ShopCartItem {
  productId: string;
  slug: string;
  nameEn: string;
  nameAr: string;
  price: number;
  sku: string;
  imageUrl?: string;
  quantity: number;
  // Set on B2B market products: the link key they were added through. The
  // server only prices B2B products for the current key, so checkout sends it.
  b2bKey?: string;
}

interface ShopCartContextValue {
  items: ShopCartItem[];
  count: number;
  subtotal: number;
  addItem: (product: Product, quantity: number, b2bKey?: string) => void;
  /** Key to send with the order when the cart holds B2B products. */
  b2bKey?: string;
  setQuantity: (productId: string, quantity: number) => void;
  decrementItem: (productId: string) => void;
  removeItem: (productId: string) => void;
  clearCart: () => void;
}

const STORAGE_KEY = "shop_cart";

const ShopCartContext = createContext<ShopCartContextValue | null>(null);

function readStoredCart(): ShopCartItem[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.filter((i) => i && i.productId && i.quantity > 0) : [];
  } catch {
    return [];
  }
}

export function ShopCartProvider({ children }: { children: React.ReactNode }) {
  const [items, setItems] = useState<ShopCartItem[]>(readStoredCart);

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
    } catch {
      // storage full/unavailable — cart just won't persist
    }
  }, [items]);

  const addItem = useCallback((product: Product, quantity: number, b2bKey?: string) => {
    setItems((prev) => {
      const existing = prev.find((i) => i.productId === product.id);
      const max = Math.max(1, product.stockQuantity);
      if (existing) {
        return prev.map((i) =>
          i.productId === product.id
            ? { ...i, quantity: Math.min(max, i.quantity + quantity) }
            : i,
        );
      }
      return [
        ...prev,
        {
          productId: product.id,
          slug: product.slug,
          nameEn: product.nameEn,
          nameAr: product.nameAr,
          price: product.price,
          sku: product.sku,
          imageUrl: product.imageUrl,
          quantity: Math.min(max, quantity),
          ...(product.market === "b2b" && b2bKey ? { b2bKey } : {}),
        },
      ];
    });
  }, []);

  const setQuantity = useCallback((productId: string, quantity: number) => {
    setItems((prev) =>
      quantity < 1
        ? prev.filter((i) => i.productId !== productId)
        : prev.map((i) => (i.productId === productId ? { ...i, quantity } : i)),
    );
  }, []);

  const decrementItem = useCallback((productId: string) => {
    setItems((prev) =>
      prev
        .map((i) => (i.productId === productId ? { ...i, quantity: i.quantity - 1 } : i))
        .filter((i) => i.quantity > 0),
    );
  }, []);

  const removeItem = useCallback((productId: string) => {
    setItems((prev) => prev.filter((i) => i.productId !== productId));
  }, []);

  const clearCart = useCallback(() => setItems([]), []);

  const value = useMemo<ShopCartContextValue>(() => {
    const count = items.reduce((sum, i) => sum + i.quantity, 0);
    const subtotal = items.reduce((sum, i) => sum + i.price * i.quantity, 0);
    const b2bKey = items.find((i) => i.b2bKey)?.b2bKey;
    return { items, count, subtotal, b2bKey, addItem, setQuantity, decrementItem, removeItem, clearCart };
  }, [items, addItem, setQuantity, decrementItem, removeItem, clearCart]);

  return <ShopCartContext.Provider value={value}>{children}</ShopCartContext.Provider>;
}

export function useShopCart(): ShopCartContextValue {
  const ctx = useContext(ShopCartContext);
  if (!ctx) throw new Error("useShopCart must be used within ShopCartProvider");
  return ctx;
}
